import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, ArrowUpRight } from "lucide-react";
import V2CompactTwin from "./V2CompactTwin";
import V2PlayerAvatar from "./V2PlayerAvatar";
import { formatScore } from "./v2OverviewUtils";
import { MUSCLE_I18N_KEYS, zoneColor } from "./v2PriorityUtils";

const WEEKDAYS = {
  fr: ["S", "D", "L", "M", "M", "J", "V"],
  en: ["S", "S", "M", "T", "W", "T", "F"],
};

export default function V2PriorityPlayerCard({
  player,
  clubId,
  date,
  period,
  gltf,
  avatarStatus,
  onSelectMuscle,
  onClearMuscle,
  selectionResetToken,
  onViewPlayer,
}) {
  const { t, i18n } = useTranslation();
  const weekdays = WEEKDAYS[i18n.language?.startsWith("fr") ? "fr" : "en"];
  const overloadCount = (player.overloadDays || []).filter(Boolean).length;
  const zone = zoneColor(player.zone);

  return (
    <article className="flex min-w-0 flex-col rounded-xl border border-[rgba(98,94,166,0.4)] bg-[rgba(11,7,53,0.55)] p-3.5">
      <header className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
        <V2PlayerAvatar
          name={player.name}
          color={player.color}
          zone={player.zone}
          size={36}
          clubId={clubId}
          playerId={player.id}
        />
        <div>
          <strong className="block text-[14px] text-[#f4f2ff]">{player.name}</strong>
          <span className="text-[12px] text-[#8b86bc]">
            {t(`v2.overview.positions.${String(player.position || "mil").toLowerCase()}`)}
            {` · ${t("v2.overview.priority.number", { number: player.number })}`}
          </span>
        </div>
        <AlertTriangle size={16} className="text-[#ff6e7d]" />
      </header>

      <div className="mb-1.5 mt-3.5 flex items-end justify-between gap-2.5">
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-[0.06em] text-[#9b96c9]">
            {t("v2.overview.trainability")}
          </span>
          <strong className="v2-display mt-0.5 block text-[34px] leading-[0.95] text-[#f6f3ff]">
            {formatScore(player.score)}
            <small className="text-[14px] text-[#8f8abf]">/10</small>
          </strong>
        </div>
        <em
          className="inline-flex items-center gap-1 rounded-full border border-current bg-[rgba(255,77,94,0.1)] px-2 py-[3px] text-[11px] font-bold not-italic"
          style={{ color: zone }}
        >
          <ArrowUpRight size={12} />
          {t(`v2.overview.${player.zone}`)}
        </em>
      </div>
      <p className="mb-3.5 text-[12px] text-[#8b86bc]">
        {player.rationaleKey ? t(player.rationaleKey) : player.rationale}
      </p>

      <div className="mb-3.5 rounded-[10px] border border-[rgba(98,94,166,0.42)] bg-[rgba(8,5,40,0.45)] px-3 py-2.5">
        <div className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.05em] text-[#c9c5f5]">
          <span>{t("v2.overview.priority.overloads_7d")}</span>
          <strong style={{ color: zone }}>{overloadCount}</strong>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {weekdays.map((day, index) => {
            const active = Boolean(player.overloadDays?.[index]);
            return (
              <span
                key={`${player.id}-day-${index}`}
                className={`flex flex-col items-center gap-0.5 py-0.5 text-[10px] ${
                  active ? "font-bold" : "text-[#8f8abf]"
                }`}
                style={active ? { color: zone } : undefined}
              >
                <b className="font-bold">{day}</b>
                <i className="not-italic opacity-80">{player.overloadDates?.[index] || ""}</i>
              </span>
            );
          })}
        </div>
        <small className="mt-2 block text-[11px] text-[#8b86bc]">
          {t("v2.overview.last_overload", { date: player.lastOverload })}
        </small>
      </div>

      <V2CompactTwin
        player={player}
        clubId={clubId}
        date={date}
        period={period}
        gltf={gltf}
        status={avatarStatus}
        onMuscleSelect={(muscle, pointer) => onSelectMuscle(player, muscle, pointer)}
        onClearMuscle={onClearMuscle}
        selectionResetToken={selectionResetToken}
      />

      <div className="mt-1.5 border-t border-[rgba(98,94,166,0.4)] pt-3">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-[#c9c5f5]">
          {t("v2.overview.priority.muscles_to_treat")}
        </h4>
        <ul className="m-0 list-none p-0">
          {(player.musclesToTreat || []).map((item) => {
            const muscleKey = MUSCLE_I18N_KEYS[item.muscle] || item.muscle;
            const color = zoneColor(item.zone);
            return (
              <li key={`${player.id}-${item.muscle}-${item.side}`}>
                <button
                  type="button"
                  className="grid w-full grid-cols-[8px_1fr_auto] items-center gap-2 rounded-md border-0 bg-transparent px-1 py-1.5 text-left hover:bg-[rgba(98,94,166,0.16)]"
                  onClick={(event) => onSelectMuscle(player, item, { x: event.clientX, y: event.clientY })}
                >
                  <i className="h-2 w-2 rounded-full" style={{ background: color }} />
                  <span className="flex flex-col text-[12px] text-[#f1efff]">
                    {`${t(`muscles.${muscleKey}`, { defaultValue: item.muscle })} · ${t(`v2.overview.tooltip.side_${item.side}`)}`}
                    <small className="text-[11px] text-[#8f8abf]">{t(`v2.overview.priority.period_${item.period}`)}</small>
                  </span>
                  <strong className="v2-display text-[16px] leading-none" style={{ color }}>
                    {formatScore(item.score)}
                  </strong>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <button
        type="button"
        className="mt-auto inline-flex w-full items-center justify-end gap-1.5 border-0 bg-transparent pt-2.5 text-[12px] font-bold text-v2-cyan"
        onClick={() => onViewPlayer?.(player)}
      >
        {t("v2.overview.priority.view_player")}
        <ArrowRight size={14} />
      </button>
    </article>
  );
}
